import React from "react";
import { useRef, useState, useEffect } from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import CountUp from "react-countup";
import image from "../../pictures/bg-presentation1.JPG";
import OfficeMap from "../OfficeMap";

import ConstructionIcon from "@mui/icons-material/Construction";
import HandymanIcon from "@mui/icons-material/Handyman";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import HardwareIcon from "@mui/icons-material/Hardware";
import { Link as RouterLink } from "react-router-dom";

function CompanyProfile() {
  const floatingTools = [
    { icon: <EngineeringIcon sx={{ fontSize: 42 }} />, className: "tool tool1" },
    { icon: <ConstructionIcon sx={{ fontSize: 46 }} />, className: "tool tool2" },
    { icon: <ArchitectureIcon sx={{ fontSize: 40 }} />, className: "tool tool3" },
    { icon: <HandymanIcon sx={{ fontSize: 44 }} />, className: "tool tool4" },
    { icon: <PrecisionManufacturingIcon sx={{ fontSize: 42 }} />, className: "tool tool5" },
    { icon: <HardwareIcon sx={{ fontSize: 40 }} />, className: "tool tool6" },
  ];

  const aboutRef = useRef(null);
const [aboutVisible, setAboutVisible] = useState(false);

useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setAboutVisible(true);
      }
    },
    { threshold: 0.3 }
  );

  if (aboutRef.current) {
    observer.observe(aboutRef.current);
  }

  return () => observer.disconnect();
}, []);

  return (
    <Box sx={{ overflowX: "hidden" }}>
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          color: "#fff",
          background:
            "radial-gradient(circle at 20% 20%, rgba(79,114,255,0.22), transparent 28%), radial-gradient(circle at 80% 30%, rgba(180,120,255,0.18), transparent 24%), linear-gradient(135deg, #05031f 0%, #0c065d 45%, #02061c 100%)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.12,
            transform: "scale(1.08)",
            filter: "blur(1px)",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(4,5,25,0.25) 0%, rgba(4,5,25,0.72) 100%)",
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            overflow: "hidden",
            zIndex: 2,
          }}
        >
          <Box className="orb orb1" />
          <Box className="orb orb2" />
          <Box className="orb orb3" />
          <Box className="orb orb4" />
          <Box className="orb orb5" />

          <Box className="cube cube1" />
          <Box className="cube cube2" />
          <Box className="cube cube3" />

          {floatingTools.map((item, i) => (
            <Box key={i} className={item.className}>
              <Box className="toolInner">{item.icon}</Box>
            </Box>
          ))}
        </Box>

        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            zIndex: 3,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            py: { xs: 10, md: 14 },
          }}
        >
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ maxWidth: 760, animation: "fadeUp 1s ease both" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "4.2rem" },
                    fontWeight: 800,
                    lineHeight: 1.08,
                    letterSpacing: "-0.02em",
                    textShadow: "0 10px 30px rgba(0,0,0,0.45)",
                  }}
                >
                  Construction With A Difference
                </Typography>

                <Typography
                  sx={{
                    mt: 2.5,
                    mb: 4,
                    maxWidth: 650,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    lineHeight: 1.8,
                    color: "rgba(255,255,255,0.88)",
                  }}
                >
                  Velvizhi Construction puts the focus back on exceptional quality,
                  transparent execution, and dependable client service.
                </Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                  component={RouterLink}
  to="/contact-us"
                    variant="contained"
                    size="large"
                    sx={{
                      background: "linear-gradient(135deg, #4f72ff 0%, #1e2d8f 100%)",
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 999,
                      boxShadow: "0 14px 30px rgba(34, 72, 255, 0.25)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #6a85ff 0%, #18256f 100%)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Get a Free Quote
                  </Button>

                  <Button
                  component={RouterLink}
                    to="/construction-plans"
                    variant="outlined"
                    size="large"
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.6)",
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 999,
                      "&:hover": {
                        background: "rgba(255,255,255,0.08)",
                        borderColor: "#fff",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    View Plans
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: "relative",
                  perspective: "1200px",
                  animation: "floatFrame 6s ease-in-out infinite",
                }}
              >
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.10)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    borderRadius: 5,
                    p: 4,
                    boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
                    transform: "rotateY(-8deg) rotateX(6deg)",
                  }}
                >
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1.5, mb: 2 }}>
                    Trusted. Transparent. Timely.
                  </Typography>

                  <Typography sx={{ color: "rgba(255,255,255,0.86)", lineHeight: 1.8 }}>
                    We blend design thinking, technical precision, and execution discipline
                    to deliver spaces that feel refined and built for long-term value.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ background: "#fff", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center" textAlign="center">
            {[
              { value: 50, label: "Projects Completed", suffix: "+" },
              { value: 10, label: "Years Experience", suffix: "+" },
              { value: 50, label: "Expert Workers", suffix: "+" },
              { value: 100, label: "Client Satisfaction", suffix: "%" },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Typography
                  sx={{
                    fontSize: { xs: "2.2rem", md: "3rem" },
                    fontWeight: 800,
                    color: "#0d0651",
                    mb: 1,
                  }}
                >
                  <CountUp end={item.value} duration={3} />
                  {item.suffix}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    fontWeight: 600,
                    color: "#222",
                  }}
                >
                  {item.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          position: "relative",
          background: "#eef0f4",
          py: { xs: 6, md: 9 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at 20% 30%, rgba(79,114,255,0.08), transparent 28%), radial-gradient(circle at 85% 70%, rgba(13,6,81,0.07), transparent 24%)",
          }}
        />
{/* Construction Background */}
<Box className="aboutConstructionBg">

  {/* Light Orbs */}
  <Box className="aboutOrb aboutOrb1" />
  <Box className="aboutOrb aboutOrb2" />

  {/* Blueprint Grid */}
  <Box className="blueprintGrid" />

  {/* Floating Construction Tools */}
  {floatingTools.map((item, i) => (
    <Box key={i} className={`aboutTool aboutTool${i + 1}`}>
      <Box className="toolInner">{item.icon}</Box>
    </Box>
  ))}

</Box>

<Container
  ref={aboutRef}
  maxWidth="lg"
  sx={{ position: "relative", zIndex: 2 }}
>
           <Typography
  className={aboutVisible ? "aboutTitle active" : "aboutTitle"}
  sx={{
    fontSize: { xs: "1.8rem", sm: "2.1rem", md: "2.5rem" },
    fontWeight: 700,
    color: "#0d0651",
    mb: 3,
  }}
>
  About Velvizhi Construction
</Typography>
<Box className="aboutTextWrapper">
  {[
    `Velvizhi Construction is a trusted and quality-driven construction company based in Chennai, committed to delivering residential, commercial, and customized building solutions with professionalism and integrity. Over the years, we have built a strong reputation for maintaining construction excellence, transparent communication, and dependable project execution across every stage of development.`,
            
           `We believe that every building is more than a structure — it is a long-term investment, a dream, and a reflection of our client’s vision. That is why we approach every project with careful planning, technical precision, and a deep commitment to understanding each client’s expectations.`,
            
            `Our strength lies in combining practical engineering knowledge with modern construction methods, high-quality materials, and efficient site management. We focus on safety, structural reliability, design coordination, and timely delivery so that each project is completed with confidence and care.`
          ].map((text, i) => (
    <Typography
      key={i}
      className={aboutVisible ? "aboutText active" : "aboutText"}
      sx={{
        color: "#4b5563",
        lineHeight: 2,
        fontSize: { xs: "1.05rem", sm: "1.12rem", md: "1.2rem" },
        textAlign: "justify",
        mb: 2,
        transitionDelay: `${i * 0.25}s`
      }}
    >
      {text}
    </Typography>
  ))}
</Box>
        </Container>
      </Box>

      <OfficeMap />

      <style>{`
      /* TITLE ANIMATION */
.aboutTitle {
  opacity: 0;
  transform: translateY(40px) rotateX(20deg);
  transition: all 0.8s ease;
}

.aboutTitle.active {
  opacity: 1;
  transform: translateY(0) rotateX(0deg);
}

/* TEXT WRAPPER (for slight depth) */
.aboutTextWrapper {
  perspective: 1000px;
}

/* PARAGRAPH ANIMATION */
.aboutText {
  opacity: 0;
  transform: translateY(30px) rotateX(10deg) scale(0.98);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.aboutText.active {
  opacity: 1;
  transform: translateY(0) rotateX(0deg) scale(1);
}

/* HOVER = subtle 3D feel */
.aboutText:hover {
  transform: translateY(-4px) scale(1.02);
}
        .orb,
        .cube,
        .tool {
          position: absolute;
          pointer-events: none;
          transform-style: preserve-3d;
        }

        .orb {
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.12);
          background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.16), rgba(255,255,255,0.02));
          backdrop-filter: blur(8px);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 40px rgba(0,0,0,0.18);
        }

        .orb1 { width: 180px; height: 180px; top: 12%; left: 6%; animation: orbFloat1 14s ease-in-out infinite; }
        .orb2 { width: 120px; height: 120px; top: 18%; right: 10%; animation: orbFloat2 11s ease-in-out infinite; }
        .orb3 { width: 220px; height: 220px; bottom: 10%; left: 8%; animation: orbFloat3 16s ease-in-out infinite; }
        .orb4 { width: 150px; height: 150px; bottom: 18%; right: 12%; animation: orbFloat4 13s ease-in-out infinite; }
        .orb5 { width: 90px; height: 90px; top: 42%; left: 50%; animation: orbFloat5 10s ease-in-out infinite; }

        .cube {
          width: 90px;
          height: 90px;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.14);
          background: linear-gradient(145deg, rgba(255,255,255,0.14), rgba(255,255,255,0.03));
          backdrop-filter: blur(12px);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 22px 50px rgba(0,0,0,0.22);
        }

        .cube::before,
        .cube::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,0.06);
        }

        .cube::before {
          transform: translate3d(12px, 12px, -18px) rotateX(60deg);
          opacity: 0.45;
        }

        .cube::after {
          transform: translate3d(-12px, -12px, 18px) rotateY(60deg);
          opacity: 0.35;
        }

        .cube1 { top: 18%; left: 18%; animation: cubeMove1 15s linear infinite; }
        .cube2 { top: 30%; right: 16%; animation: cubeMove2 18s linear infinite; }
        .cube3 { bottom: 16%; left: 50%; animation: cubeMove3 17s linear infinite; }

        .tool {
          width: 92px;
          height: 92px;
          border-radius: 24px;
          background: linear-gradient(145deg, rgba(255,255,255,0.72), rgba(230,235,255,0.45));
          border: 1px solid rgba(13, 6, 81, 0.10);
          backdrop-filter: blur(10px);
          box-shadow: 0 16px 35px rgba(13, 6, 81, 0.10);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(13, 6, 81, 0.72);
        }

        .toolInner {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          animation: toolSpin 9s ease-in-out infinite;
        }

        .tool1 { top: 6%; left: 8%; animation: toolFloat1 8s ease-in-out infinite; }
        .tool2 { bottom: 18%; left: 3%; animation: toolFloat2 9s ease-in-out infinite; }
        .tool3 { top: 36%; left: 34%; animation: toolFloat3 7.5s ease-in-out infinite; }
        .tool4 { top: 16%; right: 24%; animation: toolFloat4 8.5s ease-in-out infinite; }
        .tool5 { bottom: 10%; right: 24%; animation: toolFloat5 9s ease-in-out infinite; }
        .tool6 { top: 46%; right: 4%; animation: toolFloat6 8s ease-in-out infinite; }

       /* BACKGROUND WRAPPER */
.aboutConstructionBg {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

/* BLUEPRINT GRID EFFECT */
.blueprintGrid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(13,6,81,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(13,6,81,0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: gridMove 20s linear infinite;
}

/* LIGHT ORBS */
.aboutOrb {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(79,114,255,0.15), transparent);
  filter: blur(20px);
}

.aboutOrb1 {
  width: 200px;
  height: 200px;
  top: 10%;
  left: 10%;
  animation: orbFloat1 12s ease-in-out infinite;
}

.aboutOrb2 {
  width: 150px;
  height: 150px;
  bottom: 15%;
  right: 10%;
  animation: orbFloat2 14s ease-in-out infinite;
}

/* CONSTRUCTION TOOLS (LIGHT VERSION) */
.aboutTool {
  position: absolute;
  width: 70px;
  height: 70px;
  border-radius: 18px;
  background: rgba(255,255,255,0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d0651;
  opacity: 0.6;
}

/* TOOL POSITIONS */
.aboutTool1 { top: 10%; left: 5%; animation: toolFloat1 8s infinite; }
.aboutTool2 { bottom: 20%; left: 8%; animation: toolFloat2 9s infinite; }
.aboutTool3 { top: 40%; left: 35%; animation: toolFloat3 7s infinite; }
.aboutTool4 { top: 15%; right: 20%; animation: toolFloat4 8s infinite; }
.aboutTool5 { bottom: 10%; right: 25%; animation: toolFloat5 9s infinite; }
.aboutTool6 { top: 50%; right: 7%; animation: toolFloat6 8s infinite; }

/* GRID ANIMATION */
@keyframes gridMove {
  0% { background-position: 0 0; }
  100% { background-position: 40px 40px; }
}

/* REUSE YOUR EXISTING ANIMATIONS */-+-------
        @keyframes cubeMove1 {
          0% { transform: perspective(1000px) translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) rotateZ(0deg); }
          50% { transform: perspective(1000px) translate3d(40px, -30px, 40px) rotateX(180deg) rotateY(90deg) rotateZ(20deg); }
          100% { transform: perspective(1000px) translate3d(0, 0, 0) rotateX(360deg) rotateY(180deg) rotateZ(0deg); }
        }

        @keyframes cubeMove2 {
          0% { transform: perspective(1000px) translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg); }
          50% { transform: perspective(1000px) translate3d(-30px, 30px, 60px) rotateX(160deg) rotateY(120deg); }
          100% { transform: perspective(1000px) translate3d(0, 0, 0) rotateX(360deg) rotateY(240deg); }
        }

        @keyframes cubeMove3 {
          0% { transform: perspective(1000px) translate3d(0, 0, 0) rotateZ(0deg); }
          50% { transform: perspective(1000px) translate3d(30px, -20px, 30px) rotateZ(180deg); }
          100% { transform: perspective(1000px) translate3d(0, 0, 0) rotateZ(360deg); }
        }

        @keyframes orbFloat1 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(30px, 20px, 40px) scale(1.08); }
        }

        @keyframes orbFloat2 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(-28px, 18px, 30px) scale(1.1); }
        }

        @keyframes orbFloat3 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(20px, -24px, 50px) scale(1.12); }
        }

        @keyframes orbFloat4 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(-20px, -20px, 20px) scale(1.08); }
        }

        @keyframes orbFloat5 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(12px, 18px, 24px) scale(1.06); }
        }

        @keyframes toolFloat1 {
          0%, 100% { transform: translateY(0px) rotate(-8deg); }
          50% { transform: translateY(-14px) rotate(-2deg); }
        }

        @keyframes toolFloat2 {
          0%, 100% { transform: translateY(0px) rotate(10deg); }
          50% { transform: translateY(-12px) rotate(4deg); }
        }

        @keyframes toolFloat3 {
          0%, 100% { transform: translateY(0px) rotate(-6deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }

        @keyframes toolFloat4 {
          0%, 100% { transform: translateY(0px) rotate(8deg); }
          50% { transform: translateY(-16px) rotate(0deg); }
        }

        @keyframes toolFloat5 {
          0%, 100% { transform: translateY(0px) rotate(-10deg); }
          50% { transform: translateY(-12px) rotate(-3deg); }
        }

        @keyframes toolFloat6 {
          0%, 100% { transform: translateY(0px) rotate(7deg); }
          50% { transform: translateY(-13px) rotate(1deg); }
        }

        @keyframes toolSpin {
          0%, 100% { transform: rotateY(0deg) rotateX(0deg); }
          50% { transform: rotateY(12deg) rotateX(8deg); }
        }

        @keyframes floatFrame {
          0%, 100% { transform: translateY(0px) rotate(-0.5deg); }
          50% { transform: translateY(-10px) rotate(0.5deg); }
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 1199px) {
          .tool3, .tool6 {
            display: none;
          }
        }

        @media (max-width: 899px) {
          .tool, .orb, .cube {
            display: none;
          }
        }

        @media (prefers-reduced-m-otion: reduce) {
          *, *::before, *::after {
            animation: none !important;
            transition: none !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default CompanyProfile;